/**
 * Tenant Settings — tenant admin page.
 *
 * Workspace section: time zone (explicit Save button).
 * AI section (ADR-0069 + ADR-0126): Ask Atlas model + Background task model,
 * both auto-saving on <select> change (debounced 600ms; silent on success,
 * error toast on failure). The standalone AI Settings page was folded in here.
 * Agent access sections: user connections (a person's OAuth grant to an AI
 * client) and the agent roles those connections resolve their scopes through.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { UsTimeZoneSelect } from "../components/UsTimeZoneSelect.js";
import { SyncPolicyCard } from "../components/mirror/SyncPolicyCard.js";
import { formatTimestamp } from "../lib/formatTime.js";
import {
  AI_PROVIDER_NEW_PATH,
  aiProviderIntegrationPath,
  aiProviderLabel,
  type AiProviderStatus
} from "../lib/aiProvider.js";
import {
  scopePillLabel,
  groupScopesByFamily,
  toggleScopeSelection,
  type AgentScopeMeta,
} from "../lib/agentScopes.js";

type AiSettings = {
  /** Ask Atlas help model — registry UUID (Anthropic-only); "" = hub default. */
  help_model_id: string | null;
  /** ADR-0207: the saved help model's model_id — shown when the provider no longer serves it (never the UUID). */
  help_model_name: string | null;
  available_models: Array<{ id: string; name: string; provider: string }>;
  /** ADR-0126 background-task model override — model_id; null = platform default. */
  ai_background_model_id: string | null;
  background_models: string[];
  platform_default_background_model_id: string | null;
};

export function TenantAdminSettings() {
  const toast = useToast();

  const [tz, setTz] = useState("");
  const [tzLoading, setTzLoading] = useState(true);
  const [savingTz, setSavingTz] = useState(false);
  const [tzErr, setTzErr] = useState<string | null>(null);

  const [ai, setAi] = useState<AiSettings | null>(null);
  const [aiProvider, setAiProvider] = useState<AiProviderStatus | null>(null);
  const [aiLoading, setAiLoading] = useState(true);
  const [aiErr, setAiErr] = useState<string | null>(null);
  const helpSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bgSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const s = await apiFetch<{ timezone: string }>("/api/tenant/settings");
        setTz(s.timezone);
      } catch {
        setTz("");
      } finally {
        setTzLoading(false);
      }
    })();
    void (async () => {
      setAiErr(null);
      try {
        const [data, status] = await Promise.all([
          apiFetch<AiSettings>("/api/tenant/admin/help-settings"),
          apiFetch<AiProviderStatus>("/api/tenant/ai-provider/status")
        ]);
        setAi(data);
        setAiProvider(status);
      } catch (e) {
        setAiErr(e instanceof Error ? e.message : String(e));
      } finally {
        setAiLoading(false);
      }
    })();
  }, []);

  async function saveTz() {
    setSavingTz(true);
    setTzErr(null);
    try {
      await apiFetch("/api/tenant/settings", { method: "PATCH", body: { timezone: tz.trim() } });
      toast.push({ kind: "success", message: "Workspace timezone updated." });
    } catch (e) {
      setTzErr(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingTz(false);
    }
  }

  function handleHelpModelChange(value: string) {
    if (!ai) return;
    const newModelId = value || null;
    setAi({ ...ai, help_model_id: newModelId });
    if (helpSaveTimer.current) clearTimeout(helpSaveTimer.current);
    helpSaveTimer.current = setTimeout(() => {
      void (async () => {
        try {
          await apiFetch("/api/tenant/admin/help-settings", { method: "PATCH", body: { help_model_id: newModelId } });
        } catch (e) {
          toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
        }
      })();
    }, 600);
  }

  function handleBackgroundModelChange(value: string) {
    if (!ai) return;
    const newModelId = value || null;
    setAi({ ...ai, ai_background_model_id: newModelId });
    if (bgSaveTimer.current) clearTimeout(bgSaveTimer.current);
    bgSaveTimer.current = setTimeout(() => {
      void (async () => {
        try {
          await apiFetch("/api/tenant/admin/help-settings", {
            method: "PATCH",
            body: { ai_background_model_id: newModelId }
          });
        } catch (e) {
          toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
        }
      })();
    }, 600);
  }

  const bgValue = ai?.ai_background_model_id ?? "";
  const bgUnavailable = bgValue !== "" && ai != null && !ai.background_models.includes(bgValue);
  const helpValue = ai?.help_model_id ?? "";
  const helpUnavailable = helpValue !== "" && ai != null && !ai.available_models.some((m) => m.id === helpValue);
  // ADR-0207 §3.3: a saved model the workspace's own provider doesn't serve says so.
  const unavailableSuffix = aiProvider?.state === "connected" ? "(not available on your provider)" : "(unavailable)";

  return (
    <PageLayout title="Settings">
      <div style={{ display: "grid", gap: 12 }}>
        <div className="card" style={{ display: "grid", gap: 12 }}>
          <div style={{ fontWeight: 700 }}>Workspace timezone</div>
          <div className="muted" style={{ fontSize: 13 }}>
            US time zones only. Applies across the workspace for dates, schedules, and reporting.
          </div>
          {tzLoading ? (
            <div className="muted">Loading…</div>
          ) : (
            <>
              <label className="field">
                <span className="label">Time zone</span>
                <UsTimeZoneSelect allowEmpty emptyLabel="Select a US time zone…" value={tz} onChange={setTz} />
              </label>
              {tzErr ? <ErrorState detail={tzErr} /> : null}
              <div className="actions">
                <button
                  type="button"
                  className="btn btnPrimary"
                  disabled={savingTz || tz.trim() === ""}
                  onClick={() => void saveTz()}
                >
                  {savingTz ? "Saving…" : "Save timezone"}
                </button>
              </div>
            </>
          )}
        </div>

        <div className="card" style={{ display: "grid", gap: 12 }}>
          <div style={{ fontWeight: 700 }}>AI</div>
          {aiLoading ? (
            <div className="muted">Loading…</div>
          ) : aiErr ? (
            <ErrorState detail={aiErr} />
          ) : ai ? (
            <div style={{ display: "grid", gap: 16 }}>
              {aiProvider ? <AiProviderRow status={aiProvider} /> : null}

              <label className="field">
                <span className="label">Ask Atlas model</span>
                {ai.available_models.length === 0 && !helpUnavailable ? (
                  <div className="help">
                    {aiProvider?.state === "connected"
                      ? "This feature needs a Claude model, and this workspace's AI provider doesn't offer one."
                      : "No Anthropic models are configured. Ask your hub administrator to add one."}
                  </div>
                ) : (
                  <select
                    className="select"
                    value={ai.help_model_id ?? ""}
                    onChange={(e) => handleHelpModelChange(e.target.value)}
                  >
                    <option value="">Use hub default</option>
                    {ai.available_models.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                    {helpUnavailable ? (
                      <option value={helpValue}>{`${ai.help_model_name ?? "Saved model"} ${unavailableSuffix}`}</option>
                    ) : null}
                  </select>
                )}
                <div className="help">The model that powers the Ask Atlas help chat for all users in this tenant.</div>
              </label>

              <label className="field">
                <span className="label">Background task model</span>
                <select className="select" value={bgValue} onChange={(e) => handleBackgroundModelChange(e.target.value)}>
                  <option value="">
                    {ai.platform_default_background_model_id
                      ? `Platform default (${ai.platform_default_background_model_id})`
                      : "Platform default"}
                  </option>
                  {ai.background_models.map((mid) => (
                    <option key={mid} value={mid}>
                      {mid}
                    </option>
                  ))}
                  {bgUnavailable ? <option value={bgValue}>{`${bgValue} ${unavailableSuffix}`}</option> : null}
                </select>
                <div className="help">
                  Model used for background work (knowledge extraction, session summaries) for this tenant.
                </div>
              </label>
            </div>
          ) : null}
        </div>

        <SyncPolicyCard
          title="Shared document sync"
          apiPath="/api/tenant/admin/document-sync/shared"
          scope="shared"
          editable
          notConnectedHint="Connect Microsoft 365 under Integrations first."
        />

        <AgentAccessPanels />
      </div>
    </PageLayout>
  );
}

/**
 * ADR-0207 §3.3 — the first row of the AI card: which AI the workspace runs on.
 */
function AiProviderRow({ status }: { status: AiProviderStatus }) {
  return (
    <div className="field">
      <span className="label">AI provider</span>
      {status.state === "connected" ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {status.integration_id ? (
            <Link to={aiProviderIntegrationPath(status.integration_id)}>{aiProviderLabel(status.provider_kind)}</Link>
          ) : (
            <span>{aiProviderLabel(status.provider_kind)}</span>
          )}
          {status.last_test_status === "success" ? <span className="pill pillOk">Working</span> : null}
          {status.last_test_status === "failed" ? (
            <span className="pill pillBad">
              {status.last_test_at ? `Failing — ${formatTimestamp(status.last_test_at)}` : "Failing"}
            </span>
          ) : null}
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {status.state === "off" ? (
            <span className="pill pillBad">Off</span>
          ) : (
            <span className="pill pillWarn">Using Atlas&rsquo;s shared AI</span>
          )}
          <Link to={AI_PROVIDER_NEW_PATH}>Connect your own provider</Link>
        </div>
      )}
    </div>
  );
}

/**
 * ADR-0135: connections + roles are siblings sharing the roles list, so a
 * role edit and a connection change both read one source.
 */
function AgentAccessPanels() {
  const [roles, setRoles] = useState<AgentRole[]>([]);
  const [availableScopes, setAvailableScopes] = useState<AgentScopeMeta[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState<string | null>(null);

  const loadRoles = useCallback(async () => {
    setRolesError(null);
    try {
      const res = await apiFetch<{ agent_roles: AgentRole[]; available_scopes: AgentScopeMeta[] }>(
        "/api/tenant/admin/agent-roles"
      );
      setRoles(res.agent_roles);
      setAvailableScopes(res.available_scopes ?? []);
    } catch (e) {
      setRolesError(e instanceof Error ? e.message : String(e));
    } finally {
      setRolesLoading(false);
    }
  }, []);

  useEffect(() => { void loadRoles(); }, [loadRoles]);

  return (
    <>
      <UserConnectionsPanel roles={roles} />
      <AgentRolesPanel
        roles={roles}
        availableScopes={availableScopes}
        loading={rolesLoading}
        error={rolesError}
        onChanged={loadRoles}
      />
    </>
  );
}

// ─────────────────────────────────────────────
// Agent Roles (ADR-0135) + User connections — connect your own AI tools to
// Atlas. A person connects; their role decides their scopes, resolved live on
// every request. The Agent Keys panel was retired with the keys themselves:
// there is nothing left to mint, and a panel offering to create a credential
// the door refuses would be worse than no panel at all.
// ─────────────────────────────────────────────

type AgentRole = {
  id: string;
  name: string;
  description: string | null;
  scopes: string[];
  bound_connection_count: number;
};

function ScopePills({ scopes, scopeMeta }: { scopes: string[]; scopeMeta: AgentScopeMeta[] }) {
  if (scopes.length === 0) return <span className="muted" style={{ fontSize: 12 }}>No access</span>;
  return (
    <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
      {scopes.map((s) => {
        const m = scopeMeta.find((x) => x.value === s);
        return (
          <span key={s} className="pill" title={m?.description ?? ""}>
            {scopePillLabel(s, scopeMeta)}
          </span>
        );
      })}
    </span>
  );
}

function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : "—";
}

// ─────────────────────────────────────────────
// Agent Roles (ADR-0135 §6.1) — tenant-defined scope sets keys bind to.
// Editing a role re-powers every bound key live; scope edits are a
// deliberate, high-leverage act → explicit Save + ConfirmDialog, no autosave.
// ─────────────────────────────────────────────

type RoleEditorState = {
  /** null = creating a new role. */
  roleId: string | null;
  name: string;
  description: string;
  /** ADR-0137: selected scope values, data-driven from available_scopes. */
  scopes: string[];
};

function editorFromRole(role: AgentRole | null): RoleEditorState {
  return {
    roleId: role?.id ?? null,
    name: role?.name ?? "",
    description: role?.description ?? "",
    scopes: role ? [...role.scopes] : [],
  };
}

function editorScopes(ed: RoleEditorState): string[] {
  return ed.scopes;
}

// ─────────────────────────────────────────────
// User connections (ADR-0134, reframed by ADR-0160) — the workspace FLOOR plus
// observation of the live connections.
//
// This panel used to be where connection authority was decided: a blanket
// default role applied to whoever the policy tier let through, corrected
// person-by-person in an override list underneath. ADR-0160 inverted that — a
// person's role is assigned in advance beside their entitlement in Users &
// Teams, and connecting merely materializes it. What remains here is the
// "everyone else" floor for people with no named assignment, and a read-only
// view of who is currently connected.
// ─────────────────────────────────────────────

const CONNECTION_POLICIES: Array<{ value: string; label: string }> = [
  // ADR-0160 §5.2: `off` no longer means "nobody connects" — an assigned person
  // still does. It means there is no general access, so the label says that
  // rather than an "Off" that would now be a lie.
  { value: "off", label: "No one — assigned people only" },
  { value: "admins_builders", label: "Admins and builders" },
  { value: "all_members", label: "All members" },
];

type UserConnectionSettings = {
  policy: string;
  /** ADR-0187 §6.2 — the tenant's own connector URL slug. Null only if the tenant vanished mid-request. */
  tenant_slug?: string | null;
  default_role_id: string | null;
  default_role_name: string | null;
  interaction_capture_enabled: boolean;
};

type UserGrant = {
  id: string;
  user_display_name: string;
  user_email: string;
  connected_via: string;
  /** How this access was obtained. The app's name is self-asserted; this is not. */
  sign_in: "browser" | "device";
  /** Set when the connection exists but can no longer sign in (server-decided). */
  inert_reason: "no_role" | null;
  role_id: string | null;
  role_name: string | null;
  created_at: string;
  last_used_at: string | null;
  revoked: boolean;
  /**
   * What the connection is for. Development work is recorded, tagged, and kept
   * out of Atlas's business views; only a workspace administrator's own live
   * connection can be marked development (`purpose_editable`, server-decided).
   */
  purpose: "business" | "development";
  purpose_editable: boolean;
};

export const PURPOSE_LABEL: Record<UserGrant["purpose"], string> = { business: "Business", development: "Development" };

/**
 * The app names itself at registration and Atlas never verifies it, so "which
 * app" cannot answer "how did this access happen". This says the part Atlas
 * actually knows.
 */
function signInCopy(how: UserGrant["sign_in"]): string {
  return how === "device" ? "Code from a terminal" : "Approved in a browser";
}

function UserConnectionsPanel({ roles }: { roles: AgentRole[] }) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [policy, setPolicy] = useState("off");
  const [roleId, setRoleId] = useState<string>("");
  const [grants, setGrants] = useState<UserGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showRevoked, setShowRevoked] = useState(false);
  const [captureEnabled, setCaptureEnabled] = useState(false);
  const [tenantSlug, setTenantSlug] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [settings, grantsRes] = await Promise.all([
        apiFetch<{ user_connections: UserConnectionSettings }>("/api/tenant/admin/user-connections"),
        apiFetch<{ grants: UserGrant[] }>("/api/tenant/admin/user-connections/grants"),
      ]);
      setPolicy(settings.user_connections.policy);
      setRoleId(settings.user_connections.default_role_id ?? "");
      setCaptureEnabled(settings.user_connections.interaction_capture_enabled === true);
      setTenantSlug(settings.user_connections.tenant_slug ?? null);
      setGrants(grantsRes.grants);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Autosave (platform convention): each select persists on change — silent on
  // success, error toast on failure. The policy + role commit together (enabling
  // requires a role; the hub rejects a non-off policy with no role).
  async function persist(nextPolicy: string, nextRoleId: string) {
    setBusy(true);
    try {
      const res = await apiFetch<{ user_connections: UserConnectionSettings }>("/api/tenant/admin/user-connections", {
        method: "PATCH",
        body: { policy: nextPolicy, default_role_id: nextRoleId || null },
      });
      setPolicy(res.user_connections.policy);
      setRoleId(res.user_connections.default_role_id ?? "");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not save. Try again." });
      void load(); // re-sync to the server's truth
    } finally {
      setBusy(false);
    }
  }

  function onPolicyChange(next: string) {
    // Enabling requires a role — pre-fill Business User (the seeded default) when
    // turning connections on with none chosen yet.
    let effectiveRole = roleId;
    if (next !== "off" && !effectiveRole) {
      effectiveRole = roles.find((r) => r.name === "Business User")?.id ?? roles[0]?.id ?? "";
    }
    setPolicy(next);
    setRoleId(effectiveRole);
    void persist(next, effectiveRole);
  }

  function onRoleChange(next: string) {
    setRoleId(next);
    void persist(policy, next);
  }

  // The capture flag commits on its own — it shares nothing with policy/role but
  // a home. Turning it ON is the consequential direction and takes a confirm;
  // turning it off is the safe direction and does not.
  async function onCaptureChange(next: boolean) {
    if (next) {
      const ok = await confirm({
        title: "Start recording workplace observations?",
        detail:
          "From now on, the questions people ask Atlas and any observations their assistants report will be " +
          "recorded with their name and grouped into workspace themes that leadership can see. People are told " +
          "this on the screen where they connect. Existing records are kept if you turn this off again.",
        confirmLabel: "Turn on",
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      const res = await apiFetch<{ user_connections: { interaction_capture_enabled: boolean } }>(
        "/api/tenant/admin/user-connections/interaction-capture",
        { method: "PATCH", body: { interaction_capture_enabled: next } }
      );
      setCaptureEnabled(res.user_connections.interaction_capture_enabled === true);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not save. Try again." });
      void load();
    } finally {
      setBusy(false);
    }
  }

  // Mirrors the console's established copy affordance (ConfirmDialog): the
  // async clipboard where it exists, the textarea fallback where it does not,
  // and a transient toast either way.
  async function copyConnectorUrl(value: string) {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      toast.push({ kind: "success", message: "Copied." });
    } catch {
      toast.push({ kind: "error", message: "Copy failed." });
    }
  }

  /** Autosaves on change; silent on success, an error toast and a reload on failure. */
  async function setPurpose(grant: UserGrant, purpose: UserGrant["purpose"]) {
    setGrants((gs) => gs.map((g) => (g.id === grant.id ? { ...g, purpose } : g)));
    try {
      await apiFetch(`/api/tenant/admin/user-connections/grants/${grant.id}`, { method: "PATCH", body: { purpose } });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
      await load();
    }
  }

  async function revoke(grant: UserGrant) {
    const ok = await confirm({
      title: "Disconnect this app?",
      detail: `This ends ${grant.user_display_name}'s "${grant.connected_via}" connection immediately. They'll need to reconnect to use it again.`,
      confirmLabel: "Disconnect",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/user-connections/grants/${grant.id}/revoke`, { method: "POST" });
      toast.push({ kind: "success", message: "Connection revoked." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  const hasActiveGrants = grants.some((g) => !g.revoked);
  const revokedCount = grants.reduce((n, g) => (g.revoked ? n + 1 : n), 0);
  const visibleGrants = showRevoked ? grants : grants.filter((g) => !g.revoked);

  return (
    <div className="card" style={{ display: "grid", gap: 12 }}>
      <div style={{ fontWeight: 700 }}>User connections</div>
      {/*
        ADR-0160 §5.2 — the intro leads with the PERSON, because that is now
        where authority is decided. General access is the exception for people
        nobody has named, not the mechanism.
      */}
      <div className="muted" style={{ fontSize: 13 }}>
        People connect a personal AI assistant (like Claude) by signing in — no key to copy. Each
        connection acts as that person, with the agent role assigned to them in{" "}
        <Link to="/app/admin/users">Users &amp; Teams</Link>. General access below covers anyone
        without an assigned role.
      </div>

      {error ? (
        <ErrorState detail={error} />
      ) : loading ? (
        <div className="muted">Loading…</div>
      ) : (
        <>
          {tenantSlug ? (
            <div className="field" style={{ marginBottom: 0 }}>
              <span className="label">Connector URL</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                <span className="mono" style={{ wordBreak: "break-all" }}>
                  {`${window.location.origin}/api/mcp/${tenantSlug}`}
                </span>
                <button
                  className="btn btnSm"
                  type="button"
                  onClick={() => void copyConnectorUrl(`${window.location.origin}/api/mcp/${tenantSlug}`)}
                >
                  Copy
                </button>
              </div>
              <div className="help">
                Give this address to anyone connecting Claude or another AI client to this workspace.
              </div>
            </div>
          ) : null}

          <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <label className="field" style={{ minWidth: 240, marginBottom: 0 }}>
              <span className="label">Who can use general access</span>
              <select className="select" value={policy} disabled={busy} onChange={(e) => onPolicyChange(e.target.value)}>
                {CONNECTION_POLICIES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </label>
            <label className="field" style={{ minWidth: 220, marginBottom: 0 }}>
              <span className="label">General access role</span>
              <select
                className="select"
                value={roleId}
                disabled={busy || policy === "off"}
                onChange={(e) => onRoleChange(e.target.value)}
              >
                <option value="">Select a role…</option>
                {roles.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}</option>
                ))}
              </select>
            </label>
            <label className="field" style={{ minWidth: 220, marginBottom: 0 }}>
              <span className="label">Workplace observations</span>
              <select
                className="select"
                value={captureEnabled ? "on" : "off"}
                disabled={busy}
                onChange={(e) => void onCaptureChange(e.target.value === "on")}
              >
                <option value="off">Off</option>
                <option value="on">On</option>
              </select>
            </label>
          </div>
          <div className="help">
            {captureEnabled
              ? "Questions people ask and observations their assistants report are recorded with their name and grouped into workspace themes. People see this on the screen where they connect."
              : "Nothing is recorded from people's assistant conversations."}
          </div>
          {policy === "off" && hasActiveGrants && (
            <div className="help">
              General access is off. People with an assigned agent role can still connect, and existing
              connections below keep working until you disconnect them.
            </div>
          )}

          {/*
            ADR-0160 §5.2 — the "Roles for specific people" override block is
            gone. Per-person roles are not an exception list bolted onto a
            blanket default; they are the primary fact, assigned where the
            person is managed. One pointer, not a second control.
          */}
          <div className="help">
            Assign per-person roles in <Link to="/app/admin/users">Users &amp; Teams</Link>.
          </div>

          {revokedCount > 0 && (
            <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginBottom: 0 }}>
              <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />
              Show revoked ({revokedCount})
            </label>
          )}

          {visibleGrants.length > 0 && (
            <div className="adminList tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>App</th>
                    <th>Sign-in</th>
                    <th>Role</th>
                    <th>Purpose</th>
                    <th>Created</th>
                    <th>Last used</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {visibleGrants.map((g) => (
                    <tr key={g.id} style={g.revoked || g.inert_reason ? { opacity: 0.55 } : undefined}>
                      <td style={{ fontWeight: 600 }} title={g.user_email}>{g.user_display_name}</td>
                      <td>{g.connected_via}</td>
                      <td>{signInCopy(g.sign_in)}</td>
                      {/*
                        ADR-0160 §5.2 — observational. The role a connection was
                        created with is a FACT about that connection, not a
                        control: rebinding one grant let a person hold two
                        credentials at two authorities with no person-level fact
                        behind either. It shows its actual role, so a legacy
                        connection that diverges from its person's current
                        assignment stays visible rather than being silently
                        reconciled — the fix is one gesture away in Users &
                        Teams, or Revoke.

                        A connection whose role was deleted shows the real
                        state rather than a borrowed role name; assigning its
                        person a role repairs it.
                      */}
                      <td>
                        {g.inert_reason === "no_role"
                          ? <span className="muted">No role — can't sign in</span>
                          : g.role_name ?? <span className="muted">—</span>}
                      </td>
                      <td>
                        {g.purpose_editable ? (
                          <select
                            className="select"
                            aria-label={`Purpose of ${g.user_display_name}'s ${g.connected_via} connection`}
                            value={g.purpose}
                            onChange={(e) => void setPurpose(g, e.target.value as UserGrant["purpose"])}
                          >
                            <option value="business">{PURPOSE_LABEL.business}</option>
                            <option value="development">{PURPOSE_LABEL.development}</option>
                          </select>
                        ) : (
                          PURPOSE_LABEL[g.purpose]
                        )}
                      </td>
                      <td>{fmtDateTime(g.created_at)}</td>
                      <td>{fmtDateTime(g.last_used_at)}</td>
                      <td>
                        {g.revoked ? (
                          <span className="pill pillBad">Revoked</span>
                        ) : (
                          <button className="adminListAction isDanger" onClick={() => void revoke(g)}>Revoke</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {grants.length === 0 && <div className="muted" style={{ fontSize: 13 }}>No one has connected an app yet.</div>}
        </>
      )}
    </div>
  );
}

function AgentRolesPanel({
  roles,
  availableScopes,
  loading,
  error,
  onChanged,
}: {
  roles: AgentRole[];
  availableScopes: AgentScopeMeta[];
  loading: boolean;
  error: string | null;
  onChanged: () => Promise<void> | void;
}) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [editor, setEditor] = useState<RoleEditorState | null>(null);
  const [busy, setBusy] = useState(false);

  const editingRole = editor?.roleId ? roles.find((r) => r.id === editor.roleId) ?? null : null;
  const scopesChanged =
    editor != null &&
    editingRole != null &&
    JSON.stringify([...editorScopes(editor)].sort()) !== JSON.stringify([...editingRole.scopes].sort());

  async function save() {
    if (!editor) return;
    const name = editor.name.trim();
    if (!name) return;

    if (editor.roleId && scopesChanged && editingRole) {
      const n = editingRole.bound_connection_count;
      const ok = await confirm({
        title: `Change access for "${editingRole.name}"?`,
        detail: `Updates ${n} key${n === 1 ? "" : "s"} with this role; their live agent sessions will reconnect with the new access.`,
        confirmLabel: "Save",
      });
      if (!ok) return;
    }

    setBusy(true);
    try {
      const body = {
        name,
        description: editor.description.trim(),
        scopes: editorScopes(editor),
      };
      if (editor.roleId) {
        await apiFetch(`/api/tenant/admin/agent-roles/${editor.roleId}`, { method: "PATCH", body });
        toast.push({ kind: "success", message: `Role "${name}" saved.` });
      } else {
        await apiFetch("/api/tenant/admin/agent-roles", { method: "POST", body });
        toast.push({ kind: "success", message: `Role "${name}" created.` });
      }
      setEditor(null);
      await onChanged();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Save failed" });
    } finally {
      setBusy(false);
    }
  }

  async function deleteRole(role: AgentRole) {
    const ok = await confirm({
      title: `Delete role "${role.name}"?`,
      detail: "This cannot be undone. Keys can only bind to existing roles.",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/admin/agent-roles/${role.id}`, { method: "DELETE" });
      toast.push({ kind: "success", message: `Role "${role.name}" deleted.` });
      if (editor?.roleId === role.id) setEditor(null);
      await onChanged();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Delete failed" });
    }
  }

  return (
    <div className="card" style={{ display: "grid", gap: 12 }}>
      <div style={{ fontWeight: 700 }}>Agent Roles</div>
      <div className="muted" style={{ fontSize: 13 }}>
        Roles define what a connected AI tool can do. Every agent key binds to one role, and role
        edits apply to all of its keys immediately — no new key needed when access changes.
      </div>

      {error ? (
        <ErrorState detail={error} />
      ) : loading ? (
        <div className="muted">Loading…</div>
      ) : (
        <>
          <div className="adminList tableWrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Access</th>
                  <th>Keys</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {roles.map((r) => (
                  <tr
                    key={r.id}
                    style={{ cursor: "pointer", ...(editor?.roleId === r.id ? { background: "var(--bg)" } : {}) }}
                    onClick={() => setEditor(editorFromRole(r))}
                  >
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td>{r.description ?? ""}</td>
                    <td><ScopePills scopes={r.scopes} scopeMeta={availableScopes} /></td>
                    <td>{r.bound_connection_count}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {r.bound_connection_count > 0 ? (
                        <span className="muted" title="Revoke or re-mint this role's keys before deleting it.">
                          In use
                        </span>
                      ) : (
                        <button className="adminListAction isDanger" onClick={() => void deleteRole(r)}>
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!editor ? (
            <div>
              <button className="adminNewLink" onClick={() => setEditor(editorFromRole(null))}>
                + New role
              </button>
            </div>
          ) : (
            <div className="card" style={{ background: "var(--bg)", display: "grid", gap: 10 }}>
              <div style={{ fontWeight: 600 }}>{editor.roleId ? `Edit role: ${editingRole?.name ?? ""}` : "New role"}</div>
              <div style={{ display: "grid", gap: 10 }}>
                <label className="field">
                  <span className="label">Name</span>
                  <input
                    className="input"
                    maxLength={100}
                    value={editor.name}
                    onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                  />
                </label>
                <label className="field">
                  <span className="label">Description</span>
                  <input
                    className="input"
                    value={editor.description}
                    onChange={(e) => setEditor({ ...editor, description: e.target.value })}
                  />
                </label>

                {/* ADR-0137: Access checkboxes render from available_scopes —
                    a new tool family appears here by existing on the hub.
                    Exclusive scopes are a family's tiers (Tenant Knowledge:
                    Read / Read + propose / Read + write) and stay mutually
                    exclusive; multi scopes are independent checkboxes, including
                    an additive grant under an exclusive heading (Studies · Redact). */}
                <div style={{ display: "grid", gap: 8 }}>
                  <span className="label">Access</span>
                  {groupScopesByFamily(availableScopes).map((group) => (
                    <div key={group.family} style={{ display: "grid", gap: 4 }}>
                      <span className="label" style={{ fontSize: 12 }}>{group.family_label}</span>
                      {group.scopes.map((s) => (
                        <label
                          key={s.value}
                          style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}
                          title={s.description}
                        >
                          <input
                            type="checkbox"
                            checked={editor.scopes.includes(s.value)}
                            onChange={(e) =>
                              setEditor({
                                ...editor,
                                scopes: toggleScopeSelection(editor.scopes, s, availableScopes, e.target.checked),
                              })
                            }
                          />
                          {s.label}
                        </label>
                      ))}
                    </div>
                  ))}
                </div>

                {editor.roleId && scopesChanged && editingRole ? (
                  <div className="help">
                    Saving updates {editingRole.bound_connection_count} connection
                    {editingRole.bound_connection_count === 1 ? "" : "s"} with this role; their live agent
                    sessions will reconnect with the new access.
                  </div>
                ) : null}

                <div className="actions">
                  <button className="btn" disabled={busy} onClick={() => setEditor(null)}>
                    Cancel
                  </button>
                  <button className="btn btnPrimary" disabled={busy || !editor.name.trim()} onClick={() => void save()}>
                    {busy ? "Saving…" : editor.roleId ? "Save role" : "Create role"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
